/**
 * StompSkillEffect — Earth tree foundation active.
 *
 * The player slams the ground, sending an instantaneous shockwave that stuns
 * every enemy within 1.5 tiles for 1000ms. Zero damage. Pure CC.
 *
 * Upgrade keys (scoped to 'stomp', NOT inherited by Quake evolution):
 * - Trembling Ground: `stunRadiusTiles` (additive, +0.5 → 2.0-tile radius)
 * - Concussive: `stunDurationMs` (additive, +400ms → 1400ms stun)
 *
 * Activation breaks Hide. Fast cooldown tier (400ms).
 * Replaced by QuakeSkillEffect when `stomp_evolved` is unlocked.
 *
 * Extends InstantSkillEffect with cooldown management — overrides
 * `get cooldown()` and `update()` to track remaining cooldown.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see earth-spec.md § Foundation Node — Stomp
 */

import { InstantSkillEffect } from '../../InstantSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface StompDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
  readonly statusEffects: {
    apply(effectId: string, ...args: unknown[]): void;
  };
  /**
   * Provides a list of enemy entity IDs within a given pixel radius of the
   * player's current position. The game layer maps this to the sprite group
   * or entity manager query.
   */
  readonly targeting: {
    getEnemiesInRadius(radiusPx: number): string[];
  };
  readonly player: {
    breakHide(): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface StompConfig {
  /** Fast cooldown tier — 400ms. */
  readonly cooldownMs: number;
  /** Base stun duration before Concussive upgrade. */
  readonly baseStunDurationMs: number;
  /** Base stun radius in tiles before Trembling Ground upgrade. */
  readonly baseStunRadiusTiles: number;
  /** World units per tile — used to convert radius to pixels. */
  readonly tileSizePx: number;
}

const DEFAULT_CONFIG: StompConfig = {
  cooldownMs: 400,             // fast cooldown tier
  baseStunDurationMs: 1000,    // 1 second base stun
  baseStunRadiusTiles: 1.5,    // 1.5 tiles base radius
  tileSizePx: 32,              // TILE_SIZE from Constants
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class StompSkillEffect extends InstantSkillEffect {
  readonly skillId = 'stomp';

  private readonly deps: StompDeps;
  private readonly config: StompConfig;

  // Cooldown state
  private _cooldownRemaining = 0;

  // Per-activation telemetry (readable by scenarios)
  private _lastStunDurationMs = 0;
  private _lastRadiusPx = 0;
  private _lastStunCount = 0;
  private _hideBreakCount = 0;
  private _activationCount = 0;

  constructor(deps: StompDeps, config: Partial<StompConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get lastStunDurationMs(): number { return this._lastStunDurationMs; }
  get lastRadiusPx(): number { return this._lastRadiusPx; }
  get lastStunCount(): number { return this._lastStunCount; }
  get hideBreakCount(): number { return this._hideBreakCount; }
  get activationCount(): number { return this._activationCount; }

  // --- InstantSkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Break Hide on activation
    this.deps.player.breakHide();
    this._hideBreakCount++;

    // Resolve upgrade deltas from the UpgradeEffectResolver
    const radiusDelta = this.deps.resolver.getAdditiveEffect('stomp', 'stunRadiusTiles');
    const durationDelta = this.deps.resolver.getAdditiveEffect('stomp', 'stunDurationMs');

    const effectiveRadiusPx =
      (this.config.baseStunRadiusTiles + radiusDelta) * this.config.tileSizePx;
    const effectiveDurationMs = this.config.baseStunDurationMs + durationDelta;

    // Apply stun to every enemy in radius
    const targets = this.deps.targeting.getEnemiesInRadius(effectiveRadiusPx);
    for (const targetId of targets) {
      this.deps.statusEffects.apply('stun', effectiveDurationMs, {
        sourceSkillId: 'stomp',
        targetId,
      });
    }

    // Track telemetry
    this._lastRadiusPx = effectiveRadiusPx;
    this._lastStunDurationMs = effectiveDurationMs;
    this._lastStunCount = targets.length;
    this._activationCount++;

    // Start fast cooldown
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
